import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { FeedbackSnackbar, InlineNotice, ResourceCollection, ResourcePage, StatusBadge, useFeedbackSnackbar, type ResourceCollectionState } from '../../../design-system';
import { formatTimestampDate } from '../../../shared/formatTimestampDate';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { FeatureReleaseSettings } from '../FeatureReleaseSettings';
import { FeatureReleasePreviewDialog } from '../FeatureReleasePreviewDialog';
import { createFixtureFeatureReleasePort } from '../fixtures';
import { appReleaseLabel, featureReleaseModeLabels, isEmployeeApp, type FeatureRelease, type FeatureReleaseUser } from '../model';
import type { FeatureReleasePort } from '../port';

export type FeatureReleasePageProps = {
  port: FeatureReleasePort;
  fixtureMode: boolean;
  canManage: boolean;
  onSaved?: () => void | Promise<void>;
  onViewAs?: (user: FeatureReleaseUser) => void;
};

export function FeatureReleasePage({ port, fixtureMode, canManage, onSaved, onViewAs }: FeatureReleasePageProps) {
  const fixturePort = useMemo(createFixtureFeatureReleasePort, []);
  const activePort = fixtureMode ? fixturePort : port;
  const resource = useAsyncResource(() => activePort.list(), [activePort], '앱 공개 설정을 불러오지 못했습니다.');
  const [query, setQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [selected, setSelected] = useState<FeatureRelease>();
  const returnFocus = useRef<HTMLElement | null>(null);
  const rememberRowFocus = (event: { target: EventTarget | null }) => {
    const cell = event.target instanceof Element ? event.target.closest<HTMLElement>('[role="gridcell"]') : null;
    if (cell) returnFocus.current = cell;
  };
  useEffect(() => {
    if (selected) return;
    // 숨김 해제 스타일과 inert 변경이 반영된 프레임 뒤에 목록 초점을 복원합니다.
    let frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => { if (returnFocus.current?.isConnected) returnFocus.current.focus({ preventScroll: true }); });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selected]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { notify, feedbackProps } = useFeedbackSnackbar();
  const [refreshError, setRefreshError] = useState<string>();
  const [refreshing, setRefreshing] = useState(false);
  const refreshPending = useRef(false);
  const rows = (resource.data ?? []).filter((item) => (!modeFilter || (modeFilter === 'ADMIN_ONLY' ? !isEmployeeApp(item) : isEmployeeApp(item) && item.mode === modeFilter)) && [item.label, item.description, ...item.pages].join(' ').toLowerCase().includes(query.trim().toLowerCase())).map((item) => ({ ...item, id: item.key }));
  const columns: GridColDef<FeatureRelease & { id: string }>[] = [
    { field: 'label', headerName: '앱', width: 180, renderCell: ({ row }) => <Typography variant="body2" title={row.label} noWrap sx={{ fontWeight: 700 }}>{row.label}</Typography> },
    { field: 'mode', headerName: '구성원 공개 범위', width: 154, renderCell: ({ row }) => <StatusBadge label={appReleaseLabel(row)} tone={!isEmployeeApp(row) ? 'neutral' : row.mode === 'ALL' ? 'success' : row.mode === 'SELECTED' ? 'info' : 'neutral'} /> },
    { field: 'employeePages', headerName: '구성원 기능', minWidth: 180, flex: 1, valueGetter: (_value, row) => row.employeePages.join(' · ') || '구성원 화면 없음' },
    { field: 'adminPages', headerName: '관리자 기능', minWidth: 220, flex: 1, valueGetter: (_value, row) => row.adminPages.join(' · ') },
    { field: 'personIds', headerName: '선택 대상 수', width: 130, type: 'number', valueGetter: (_value, row) => isEmployeeApp(row) && row.mode === 'SELECTED' ? row.personIds.length : null, valueFormatter: (value) => value === null ? '-' : `${value}명` },
    { field: 'updatedAt', headerName: '최종 변경일', width: 132, valueGetter: (_value, row) => row.updatedAt ? formatTimestampDate(row.updatedAt) : '기본 설정' },
  ];
  const state: ResourceCollectionState = resource.loading ? { kind: 'loading' } : resource.error ? { kind: 'error', message: resource.error.message, onRetry: resource.reload } : rows.length ? { kind: 'ready' } : { kind: 'empty', title: '조건에 맞는 앱이 없습니다.', action: <Button onClick={() => { setQuery(''); setModeFilter(''); }}>검색 초기화</Button> };
  const refreshAfterSave = async () => {
    if (refreshPending.current) return;
    refreshPending.current = true;
    setRefreshing(true);
    setRefreshError(undefined);
    resource.reload();
    try {
      await onSaved?.();
    } catch {
      setRefreshError('설정은 저장됐지만 화면을 갱신하지 못했습니다. 다시 조회해 주세요.');
    } finally {
      refreshPending.current = false;
      setRefreshing(false);
    }
  };
  const saved = () => {
    setSelected(undefined);
    notify('공개 설정을 저장했습니다.', { tone: 'success' });
    void refreshAfterSave();
  };
  return <Box sx={{ position: 'relative', height: '100%', minHeight: 0, minWidth: 0 }}>
    <Box onClickCapture={rememberRowFocus} onKeyDownCapture={rememberRowFocus} inert={Boolean(selected) || undefined} aria-hidden={Boolean(selected) || undefined} sx={{ height: '100%', minHeight: 0, minWidth: 0, position: selected ? 'absolute' : 'relative', inset: selected ? 0 : 'auto', visibility: selected ? 'hidden' : 'visible' }}>
    <ResourcePage title="앱 공개" help="앱에 포함된 구성원 기능의 공개 범위를 함께 설정합니다. 관리자 화면은 기존 관리 권한에 따라 사용할 수 있습니다." primaryAction={<Button variant="fill" onClick={() => setPreviewOpen(true)}>사용자별 확인</Button>}>
      {refreshError && <InlineNotice severity="warning" announcement="polite" action={<Button disabled={refreshing} onClick={() => void refreshAfterSave()}>다시 조회</Button>}>{refreshError}</InlineNotice>}
      <ResourceCollection
        state={state} rows={rows} columns={columns} totalCount={resource.data?.length ?? 0}
        query={query} onQueryChange={setQuery} searchLabel="앱 검색" searchPlaceholder="앱 이름·포함 화면 검색" ariaLabel="앱 공개 목록"
        filters={[{ label: '구성원 공개 범위', value: modeFilter, onChange: setModeFilter, options: [{ label: '전체', value: '' }, ...Object.entries(featureReleaseModeLabels).map(([value, label]) => ({ value, label })), { label: '관리자 전용', value: 'ADMIN_ONLY' }] }]}
        onRowClick={({ row }) => setSelected(row)}
      />
    </ResourcePage>
    </Box>
    {selected && <FeatureReleaseSettings key={selected.key} feature={selected} port={activePort} canManage={canManage} onClose={() => setSelected(undefined)} onSaved={saved} />}
    {previewOpen && <FeatureReleasePreviewDialog port={activePort} onClose={() => setPreviewOpen(false)} onViewAs={onViewAs ? (user) => { setPreviewOpen(false); onViewAs(user); } : undefined} />}
    <FeedbackSnackbar {...feedbackProps} />
  </Box>;
}
